import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useI18n, LangSwitch } from "@/lib/i18n";
import {
  SignIn, SignOut, ForkKnife, Coffee, Cigarette, Briefcase, House,
  Lock, ArrowRight, Fingerprint, Scales
} from "@phosphor-icons/react";

const KIOSK_KEY_STORAGE = "attendio_kiosk_key";

const ACTIONS = [
  { type: "ARRIVE", labelKey: "arrive", color: "bg-[#00CC33] text-black", icon: SignIn },
  { type: "LEAVE", labelKey: "leave_office", color: "bg-[#FF3333] text-white", icon: SignOut },
  { type: "LUNCH_START", labelKey: "lunch_start", color: "bg-[#FFCC00] text-black", icon: ForkKnife },
  { type: "LUNCH_END", labelKey: "lunch_end", color: "bg-white text-black", icon: ForkKnife },
  { type: "COFFEE_START", labelKey: "coffee", color: "bg-[#FFCC00] text-black", icon: Coffee },
  { type: "COFFEE_END", labelKey: "coffee_end", color: "bg-white text-black", icon: Coffee },
  { type: "CIGARETTE_START", labelKey: "cigarette", color: "bg-[#FFCC00] text-black", icon: Cigarette },
  { type: "CIGARETTE_END", labelKey: "cigarette_end", color: "bg-white text-black", icon: Cigarette },
  { type: "BUSINESS_TRIP_START", labelKey: "business_trip", color: "bg-[#FF8000] text-black", icon: Briefcase },
  { type: "BUSINESS_TRIP_END", labelKey: "business_trip_end", color: "bg-white text-black", icon: Briefcase },
  { type: "WFH_START", labelKey: "wfh_start", color: "bg-[#FF66B2] text-black", icon: House },
  { type: "WFH_END", labelKey: "wfh_end", color: "bg-white text-black", icon: House },
  { type: "COMPENSATION_LEAVE", labelKey: "compensation_leave", color: "bg-[#0047FF] text-white", icon: Scales },
];

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export default function KioskPage() {
  const { t, lang } = useI18n();
  const now = useClock();

  const [kioskKey, setKioskKey] = useState(() => localStorage.getItem(KIOSK_KEY_STORAGE) || "");
  const [keyInput, setKeyInput] = useState("");
  const [keyError, setKeyError] = useState("");

  const [nfc, setNfc] = useState("");
  const [scanMode, setScanMode] = useState(true);
  const [current, setCurrent] = useState(null); // { user, last_event } or null
  const [message, setMessage] = useState(null); // { text, tone }
  const inputRef = useRef(null);
  const resetTimerRef = useRef(null);

  const timeStr = useMemo(
    () =>
      now.toLocaleTimeString(lang === "sl" ? "sl-SI" : "en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    [now, lang]
  );
  const dateStr = useMemo(
    () =>
      now.toLocaleDateString(lang === "sl" ? "sl-SI" : "en-GB", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      }),
    [now, lang]
  );

  useEffect(() => {
    if (!kioskKey) return;
    if (scanMode && inputRef.current) inputRef.current.focus();
  }, [kioskKey, scanMode, current]);

  const unlock = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) {
      setKeyError("Enter kiosk API key");
      return;
    }
    localStorage.setItem(KIOSK_KEY_STORAGE, keyInput.trim());
    setKioskKey(keyInput.trim());
    setKeyInput("");
    setKeyError("");
  };

  const resetSoon = (ms = 6000) => {
    if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setCurrent(null);
      setMessage(null);
      setNfc("");
      setScanMode(true);
    }, ms);
  };

  const lookup = async (uid) => {
    const cleaned = (uid || "").trim();
    if (!cleaned) return;
    try {
      const { data } = await axios.post(
        `${API}/kiosk/lookup`,
        { nfc_uid: cleaned },
        { headers: { "X-Kiosk-Key": kioskKey } }
      );
      if (data.enrolled) {
        // Card was auto-enrolled to a waiting employee
        setMessage({
          text: `CARD LINKED · ${data.user.name}`,
          tone: "success",
        });
        document.body.classList.add("kiosk-flash");
        setTimeout(() => document.body.classList.remove("kiosk-flash"), 900);
        resetSoon(3500);
        return;
      }
      setCurrent(data);
      setScanMode(false);
      setMessage({ text: `${t("welcome")}, ${data.user.name}`, tone: "info" });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Badge not recognised";
      if (msg === "Invalid kiosk key") {
        localStorage.removeItem(KIOSK_KEY_STORAGE);
        setKioskKey("");
        return;
      }
      setMessage({ text: msg, tone: "error" });
      resetSoon(2500);
    }
  };

  const tap = async (eventType) => {
    if (!current) return;
    try {
      const { data } = await axios.post(
        `${API}/kiosk/tap`,
        { nfc_uid: current.user.nfc_uid, event_type: eventType },
        { headers: { "X-Kiosk-Key": kioskKey } }
      );
      const successLabel = t(ACTIONS.find((a) => a.type === eventType)?.labelKey || "");
      setMessage({ text: `${successLabel} · ${data.user.name}`, tone: "success" });
      document.body.classList.add("kiosk-flash");
      setTimeout(() => document.body.classList.remove("kiosk-flash"), 900);
      resetSoon(3500);
    } catch (e) {
      setMessage({ text: e?.response?.data?.detail || "Error", tone: "error" });
      resetSoon(3000);
    }
  };

  const onManualSubmit = (e) => {
    e.preventDefault();
    lookup(nfc);
  };

  // ------- Unlock screen -------
  if (!kioskKey) {
    return (
      <div className="min-h-screen bg-[#F4F4F0] flex items-center justify-center p-6">
        <form
          onSubmit={unlock}
          className="brut-border bg-white p-10 brut-shadow-lg max-w-lg w-full"
          data-testid="kiosk-unlock-form"
        >
          <div className="flex items-center gap-3 mb-6">
            <Lock size={32} weight="bold" />
            <div className="font-display font-black text-3xl tracking-tighter">{t("kiosk_unlock")}</div>
          </div>
          <label className="block text-xs font-bold uppercase tracking-wider mb-2">{t("kiosk_key")}</label>
          <input
            data-testid="kiosk-key-input"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            type="password"
            className="w-full brut-border p-3 mb-2 focus:outline-none focus:border-[#0047FF]"
            placeholder="••••••••••••••"
            autoFocus
          />
          {keyError && <div className="text-[#FF3333] text-sm font-bold mb-2">{keyError}</div>}
          <button
            type="submit"
            data-testid="kiosk-unlock-submit"
            className="w-full bg-black text-white brut-border brut-shadow brut-hover p-4 font-display font-black text-xl tracking-tighter uppercase"
          >
            {t("unlock")}
          </button>
          <div className="mt-4 text-xs font-mono text-black/60">
            Dev key: <span className="font-bold">kiosk-dev-key-change-me</span>
          </div>
        </form>
      </div>
    );
  }

  // ------- Scan / Action screen -------
  return (
    <div className="min-h-screen bg-[#F4F4F0] p-6 md:p-10">
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="flex items-center gap-3">
            <Fingerprint size={32} weight="bold" />
            <div className="font-display font-black text-2xl tracking-tighter">ATTENDIO · KIOSK</div>
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] mt-1 text-black/60">
            {dateStr}
          </div>
        </div>
        <div className="flex gap-3">
          <LangSwitch />
          <button
            data-testid="kiosk-logout-btn"
            onClick={() => {
              localStorage.removeItem(KIOSK_KEY_STORAGE);
              setKioskKey("");
            }}
            className="text-xs font-bold uppercase tracking-wider brut-border px-3 py-1 bg-white hover:bg-[#FFCC00]"
          >
            exit kiosk
          </button>
        </div>
      </div>

      {/* Giant clock */}
      <div
        className="font-display font-black leading-[0.85] tracking-tighter select-none"
        style={{ fontSize: "clamp(6rem, 18vw, 22rem)" }}
        data-testid="kiosk-clock"
      >
        {timeStr}
      </div>

      {/* Content */}
      {scanMode ? (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-7 brut-border bg-white p-8 brut-shadow-lg">
            <div className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-none">
              {t("tap_badge")}
            </div>
            <div className="mt-6 flex items-center gap-4">
              <div className="w-16 h-16 brut-border bg-[#0047FF] pulse-ring" />
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/70">
                {t("scan_badge")} · NFC / RFID
              </div>
            </div>
          </div>

          <form
            onSubmit={onManualSubmit}
            className="md:col-span-5 brut-border bg-white p-8 brut-shadow"
            data-testid="kiosk-manual-form"
          >
            <label className="block text-xs font-bold uppercase tracking-wider mb-2">{t("manual_entry")}</label>
            <input
              ref={inputRef}
              data-testid="kiosk-nfc-input"
              value={nfc}
              onChange={(e) => setNfc(e.target.value)}
              className="w-full brut-border p-4 text-2xl font-mono focus:outline-none focus:border-[#0047FF]"
              placeholder="EMP-101"
              autoFocus
            />
            <button
              type="submit"
              data-testid="kiosk-manual-submit"
              className="mt-4 w-full bg-[#0047FF] text-white brut-border brut-shadow brut-hover p-4 font-display font-black text-xl tracking-tighter uppercase flex items-center justify-center gap-2"
            >
              {t("submit")} <ArrowRight size={22} weight="bold" />
            </button>
            {message && (
              <div
                data-testid="kiosk-message"
                className={`mt-4 brut-border p-3 text-sm font-bold ${
                  message.tone === "error"
                    ? "bg-[#FF3333] text-white"
                    : "bg-[#FFCC00] text-black"
                }`}
              >
                {message.text}
              </div>
            )}
          </form>
        </div>
      ) : (
        <div className="mt-8">
          <div className="brut-border bg-white p-6 brut-shadow-lg flex items-center justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/60">{t("welcome")}</div>
              <div className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-none" data-testid="kiosk-user-name">
                {current?.user?.name}
              </div>
              <div className="text-xs font-mono mt-2">
                {current?.user?.email} · {current?.user?.team || "—"}
              </div>
            </div>
            <div>
              {current?.last_event && (
                <div className="tag bg-[#FFCC00]" data-testid="kiosk-last-event">
                  {current.last_event.event_type.replace(/_/g, " ")}
                </div>
              )}
            </div>
          </div>

          {message && (
            <div
              data-testid="kiosk-message"
              className={`mt-4 brut-border p-3 text-sm font-bold ${
                message.tone === "error"
                  ? "bg-[#FF3333] text-white"
                  : message.tone === "success"
                  ? "bg-[#00CC33] text-black"
                  : "bg-[#FFCC00] text-black"
              }`}
            >
              {message.text}
            </div>
          )}

          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            {ACTIONS.map((a) => (
              <button
                key={a.type}
                data-testid={`kiosk-action-${a.type.toLowerCase()}`}
                onClick={() => tap(a.type)}
                className={`kiosk-btn ${a.color}`}
              >
                <a.icon size={44} weight="bold" />
                <div className="font-display font-black text-2xl tracking-tighter uppercase leading-none">
                  {t(a.labelKey)}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-70 font-mono">
                  {a.type}
                </div>
              </button>
            ))}
          </div>

          <button
            onClick={() => {
              setCurrent(null);
              setScanMode(true);
              setMessage(null);
              setNfc("");
            }}
            data-testid="kiosk-cancel-btn"
            className="mt-6 brut-border bg-white brut-shadow brut-hover px-4 py-2 font-bold uppercase text-xs tracking-wider"
          >
            {t("cancel")}
          </button>
        </div>
      )}
    </div>
  );
}
